import {
  isValidationError,
  isPermissionError,
  isDuplicateError,
  isServerError,
  AUTH_PERMISSION_DENIED,
  DATA_DUPLICATE_ENTRY
} from './errorTypes.js';

/**
 * Get HTTP status message fallback for legacy responses
 * @param {number} status - HTTP status code
 * @returns {string} Human-readable status message
 */
export function getHttpStatusMessage(status) {
  switch (status) {
    case 400:
      return 'Invalid request';
    case 401:
      return 'Session expired. Please log in again.';
    case 403:
      return 'Access denied';
    case 404:
      return 'Resource not found';
    case 409:
      return 'Conflict - resource already exists';
    case 422:
      return 'Validation failed';
    case 500:
      return 'Server error';
    case 502:
    case 503:
    case 504:
      return 'Service temporarily unavailable';
    default:
      return 'An error occurred';
  }
}

/**
 * Parse error response from axios error object
 * Handles both new ErrorResponse format and legacy response formats
 * @param {Object} axiosError - Axios error object
 * @returns {import('./errorTypes.js').ErrorResponse} Parsed error response
 */
export function parseErrorResponse(axiosError) {
  // Check if response exists
  if (!axiosError || !axiosError.response) {
    return {
      code: null,
      message: 'Network error occurred',
      details: null,
      timestamp: new Date().toISOString(),
      errorId: null,
      httpStatus: null
    };
  }

  const { status, data } = axiosError.response;

  // Check if this is a new ErrorResponse format (has 'code' field)
  if (data && typeof data.code === 'string') {
    // New ErrorResponse format
    return {
      code: data.code,
      message: data.message || 'An error occurred',
      details: data.details || null,
      timestamp: data.timestamp || new Date().toISOString(),
      errorId: data.errorId || null,
      httpStatus: status
    };
  }

  // Legacy response format - extract message from various possible fields
  let legacyMessage = null;
  if (data) {
    if (data.Reason) {
      legacyMessage = data.Reason;
    } else if (data.errorMessage) {
      legacyMessage = data.errorMessage;
    } else if (typeof data === 'string') {
      legacyMessage = data;
    }
  }

  // Fallback to HTTP status message if no legacy message found
  const message = legacyMessage || getHttpStatusMessage(status);

  return {
    code: null, // null indicates legacy response
    message: message,
    details: null,
    timestamp: new Date().toISOString(),
    errorId: null,
    httpStatus: status
  };
}

/**
 * Extract field-level validation errors from error response
 * @param {import('./errorTypes.js').ErrorResponse} errorResponse - Parsed error response
 * @returns {import('./errorTypes.js').FieldError[]} Array of field errors, or empty array
 */
export function getFieldErrors(errorResponse) {
  if (!errorResponse) {
    return [];
  }

  // Check if this is a validation error with field details
  if (!isValidationError(errorResponse.code)) {
    return [];
  }

  // Extract fields array from details
  if (errorResponse.details && Array.isArray(errorResponse.details.fields)) {
    return errorResponse.details.fields.map(field => ({
      field: field.field || '',
      code: field.code || '',
      message: field.message || ''
    }));
  }

  return [];
}

/**
 * Extract permission details from error response
 * @param {import('./errorTypes.js').ErrorResponse} errorResponse - Parsed error response
 * @returns {{requiredPrivilege: string}|null} Permission details or null
 */
export function getPermissionDetails(errorResponse) {
  if (!errorResponse) {
    return null;
  }

  // Check if this is a permission error
  if (!isPermissionError(errorResponse.code)) {
    return null;
  }

  // Extract required privilege from details
  if (errorResponse.details && errorResponse.details.requiredPrivilege) {
    return {
      requiredPrivilege: errorResponse.details.requiredPrivilege
    };
  }

  return null;
}

/**
 * Extract duplicate entry details from error response
 * @param {import('./errorTypes.js').ErrorResponse} errorResponse - Parsed error response
 * @returns {{resourceType: string, identifier: string}|null} Duplicate details or null
 */
export function getDuplicateDetails(errorResponse) {
  if (!errorResponse) {
    return null;
  }

  // Check if this is a duplicate error
  if (!isDuplicateError(errorResponse.code)) {
    return null;
  }

  // Extract resource type and identifier from details
  if (errorResponse.details) {
    return {
      resourceType: errorResponse.details.resourceType || '',
      identifier: errorResponse.details.identifier || ''
    };
  }

  return null;
}

/**
 * Get display message for error response with i18n support
 * @param {import('./errorTypes.js').ErrorResponse} errorResponse - Parsed error response
 * @param {Object} i18n - i18n instance with t() method for translations
 * @returns {string} Localized error message
 */
export function getDisplayMessage(errorResponse, i18n) {
  if (!errorResponse) {
    return 'An error occurred';
  }

  let message = errorResponse.message;

  // Try to get translation for error code if available
  if (errorResponse.code && i18n && typeof i18n.t === 'function') {
    const translation = i18n.t(errorResponse.code);
    // Check if translation was found (i18n usually returns key if not found)
    if (translation !== errorResponse.code) {
      message = translation;
    }
  }

  // For server errors, append errorId if available
  if (errorResponse.code && isServerError(errorResponse.code) && errorResponse.errorId) {
    message = `${message} Reference: ${errorResponse.errorId}`;
  }

  return message;
}
